import Vue from 'vue';
import Vuex from 'vuex';
import * as api from '../api';
import drieZesNegen from './modules/3-6-9';
import openDeur from './modules/openDeur';
import puzzel from './modules/puzzel';
import galerij from './modules/galerij';
import collectiefGeheugen from './modules/collectiefGeheugen';
import finale from './modules/finale';
import { minBy as _minBy } from 'lodash';
import { socketService } from '../api/socketService';
import screenfull from 'screenfull';

const NR_OF_COLORS = 12;

// Set once a first full game state was applied or published. After that the
// host is the source of truth and ignores incoming snapshots.
let hydrated = false;

Vue.use(Vuex);

const store = new Vuex.Store({
  strict: process.env.NODE_ENV !== 'production',
  modules: {
    rounds: {
      modules: {
        drieZesNegen,
        openDeur,
        puzzel,
        galerij,
        collectiefGeheugen,
        finale
      }
    }
  },
  state: {
    gameId: null, // LOCAL STATE
    name: null,
    homePage: process.env.VUE_APP_DSMTW_WEBSITE,
    pinCode: null,
    premium: false, // Whether the user is playing the free version or not
    plan: null, // Which premium plan did the user buy?
    bestPlayersInFinale: false,
    screenMode: false, // LOCAL STATE: 0 = "quizmaster", 1 = "participants"
    questionsPerRound: [], // The quiz data that contains all the questions and image urls.
    showEditScores: false,
    isFullscreen: false, // whether fullscreen is enabled
    players: [], // The names of all players
    game: {
      bgColor: 0,
      activePlayerId: false,
      winnerId: false, // the id of the player who won the finale
      clockStarted: false,
      currentQuestion: 0,
      currentRound: 0,
      foundKeywords: [],
      players: [],
      reviewMode: false,
      state: 0
      /*
        Shared game state: which round, who's turn, ...
        state =>
          0 = lobby
          1 = startScreen of current round
          2 = which player's turn
          3 = game over -> someone won the game
          4 = playing (show whatever the current round needs to show)
      */
    }
  },
  actions: {
    fetchQuiz: ({ commit, state }, quizId) => {
      if (state.gameId) {
        return Promise.resolve(state.gameId);
      } else {
        return new Promise((resolve, reject) => {
          api
            .fetchQuiz(quizId)
            .then(quiz => {
              commit('SET_QUIZ', quiz);
              resolve(quiz.gameId);
            })
            .catch(reject);
        });
      }
    },
    syncGame: ({ state }) => {
      const { game, rounds } = state;
      hydrated = true;
      socketService.broadcast('game-state', { game, rounds });
    },
    watchGameStateChanges: ({ commit, getters, dispatch }) => {
      // The server replays the last game-state right after connecting, so
      // late joiners and a refreshed host start in sync.
      socketService.listen('game-state', gameState => {
        if (getters.isHost && hydrated) return;
        hydrated = true;
        commit('SET_GAME', gameState);
      });
      // After a reconnect the host's local state is the most recent:
      // republish so the server snapshot and all players catch up.
      socketService.onReconnect(() => {
        if (getters.isHost) dispatch('syncGame');
      });
    },
    startGame: ({ commit, dispatch }) => {
      commit('ACTIVATE_ROUND', 0);
      dispatch('resetRound');
    },
    previousRound: ({ commit, dispatch }) => {
      commit('PREVIOUS_ROUND');
      dispatch('resetRound');
    },
    nextRound: ({ commit, dispatch }) => {
      commit('NEXT_ROUND');
      dispatch('resetRound');
    },
    resetRound: ({ commit }) => {
      commit('SHOW_ROUND_START_SCREEN');
      commit('STOP_CLOCK');
      commit('RESET_ALL_PLAYERS_TURNS');
      commit('DEACTIVATE_PLAYER');
      commit('RESET_FOUND_KEYWORDS');
      commit('RESET_ALL_PLAYERS_FIRST_TURNS');
      commit('DISABLE_REVIEW_MODE');
    },
    openHostScreen: ({ commit, state }, pinCode) => {
      if (pinCode === state.pinCode) commit('ACTIVATE_HOST_SCREEN');
    },
    // -> Async action
    startClockForActivePlayer: ({ state, commit, getters }) => {
      return new Promise(resolve => {
        if (getters.activePlayer && !getters.clockIsRunning) {
          api.startClock(state.gameId).then(clockStarted => {
            commit('SET_CLOCK_TIME', clockStarted);
            resolve(clockStarted);
          });
        } else {
          resolve(true);
        }
      });
    },
    enableReviewMode: ({ commit }) => {
      commit('DEACTIVATE_PLAYER');
      commit('ENABLE_REVIEW_MODE');
    },
    endActivePlayerTurn: ({ commit, getters }) => {
      if (getters.activePlayer) {
        commit('END_PLAYER_TURN', getters.activePlayer.id);
      }
    },
    toggleFullscreen: ({ commit }) => {
      if (screenfull.enabled) {
        screenfull.toggle();
        commit('TOGGLE_FULLSCREEN');
      }
    },
    changeBgColor: ({ commit, state }) => {
      let colorId = state.game.bgColor + 1;
      if (colorId < 0 || colorId >= NR_OF_COLORS) {
        colorId = 0;
      }
      commit('SET_BG_COLOR', colorId);
    }
  },
  mutations: {
    SET_GAME: (state, gameState) => {
      state = Object.assign(state, gameState);
    },
    SET_QUIZ: (state, quiz) => {
      state = api.mapQuiz(state, quiz);
    },
    // --> the screen to edit the player's scores
    TOGGLE_EDIT_SCORES_SCREEN: state => {
      state.showEditScores = !state.showEditScores;
    },
    // --> the opening screen of every round
    SHOW_ROUND_START_SCREEN: state => {
      state.game.state = 1;
    },
    // --> the screen that show who's turn it is
    SHOW_PLAYER_TURN_SCREEN: state => {
      state.game.state = 2;
    },
    // --> the screen that shows who won the finale
    SHOW_GAME_OVER_SCREEN: state => {
      state.game.state = 3;
    },
    // --> show the current round's screens
    SHOW_ROUND_PLAYSCREEN: state => {
      state.game.state = 4;
    },
    // --> the host that can control the whole game
    ACTIVATE_HOST_SCREEN: state => {
      state.screenMode = 0;
    },
    // --> the players who can just follow the game
    ACTIVATE_PLAYERS_SCREEN: state => {
      state.screenMode = 1;
    },
    ACTIVATE_ROUND: (state, roundId) => {
      state.game.currentRound = roundId;
    },
    PREVIOUS_ROUND: state => {
      state.game.currentRound--;
    },
    NEXT_ROUND: state => {
      state.game.currentRound++;
    },
    NEXT_QUESTION: state => {
      state.game.currentQuestion++;
    },
    SET_CURRENT_QUESTION: (state, questionId) => {
      state.game.currentQuestion = questionId;
    },
    ACTIVATE_PLAYER: (state, playerId) => {
      state.game.activePlayerId = playerId;
    },
    DEACTIVATE_PLAYER: state => {
      state.game.activePlayerId = false;
    },
    END_PLAYER_TURN: (state, playerId) => {
      state.game.players[playerId].tookTurn = true;
    },
    END_PLAYER_FIRST_TURN: (state, playerId) => {
      state.game.players[playerId].tookFirstTurn = true;
    },
    RESET_ALL_PLAYERS_TURNS: state => {
      state.game.players.forEach(player => {
        player.tookTurn = false;
      });
    },
    RESET_ALL_PLAYERS_FIRST_TURNS: state => {
      state.game.players.forEach(player => {
        player.tookFirstTurn = false;
      });
    },
    REWARD_PLAYER: (state, { playerId, score }) => {
      state.game.players[playerId].score += score;
      if (state.game.players[playerId].score < 0) {
        state.game.players[playerId].score = 0;
      }
    },
    COUNTDOWN_SCORE_PLAYER: (state, playerId) => {
      state.game.players[playerId].score--;
      if (state.game.players[playerId].score < 0) {
        state.game.players[playerId].score = 0;
      }
    },
    CHANGE_SCORE_OF_PLAYER: (state, { playerId, score }) => {
      state.game.players[playerId].score = score;
    },
    SET_CLOCK_TIME: (state, startedAt) => {
      state.game.clockStarted = startedAt;
    },
    STOP_CLOCK: state => {
      state.game.clockStarted = false;
    },
    ENABLE_REVIEW_MODE: state => {
      state.game.reviewMode = true;
    },
    DISABLE_REVIEW_MODE: state => {
      state.game.reviewMode = false;
    },
    RESET_FOUND_KEYWORDS: state => {
      state.game.foundKeywords = [];
    },
    MARK_KEYWORD_AS_FOUND: (state, { keywordId, playerId }) => {
      state.game.foundKeywords.push({ keywordId, playerId });
    },
    SET_WINNER: (state, playerId) => {
      state.game.winnerId = playerId;
    },
    TOGGLE_FULLSCREEN: state => {
      state.isFullscreen = !state.isFullscreen;
    },
    SET_BG_COLOR: (state, colorId) => {
      state.game.bgColor = colorId;
    }
  },
  getters: {
    screenModeIsSet(state) {
      return state.screenMode !== false;
    },
    isHost(state) {
      return state.screenMode === 0;
    },
    isPremium(state) {
      return state.premium;
    },
    showTeaser(state, getters) {
      return (
        state.plan &&
        state.plan === 'maatje' &&
        (getters.showLobby || getters.showRoundIntro || getters.showGameOver)
      );
    },
    showLobby(state) {
      return state.game.state === 0;
    },
    showEditScores(state) {
      return state.showEditScores === true;
    },
    showRoundIntro(state) {
      return state.game.state === 1;
    },
    showPlayersTurn(state) {
      return state.game.state === 2;
    },
    showGameOver(state) {
      return state.game.state === 3;
    },
    players(state) {
      return state.game.players.map((player, id) => {
        return Object.assign({ name: state.players[id] }, player);
      });
    },
    playersWithOpenTurns(state, getters) {
      return getters.players.filter(player => !player.tookTurn);
    },
    playersWithoutOpenTurns(state, getters) {
      return getters.players.filter(player => player.tookTurn);
    },
    // the players who did not yet start a question
    playersWithoutFirstTurn(state, getters) {
      return getters.players.filter(player => !player.tookFirstTurn);
    },
    // the players who already started a question
    playersWithFirstTurn(state, getters) {
      return getters.players.filter(player => player.tookFirstTurn);
    },
    inactivePlayers(state, getters) {
      if (getters.activePlayer) {
        return getters.players.filter(
          player => player.id !== getters.activePlayer.id
        );
      } else {
        return getters.players;
      }
    },
    activePlayer(state, getters) {
      return getters.players[state.game.activePlayerId];
    },
    winningPlayer(state, getters) {
      return getters.players[state.game.winnerId];
    },
    nrOfPlayers(state, getters) {
      return getters.players.length;
    },
    currentRound(state) {
      return state.questionsPerRound[state.game.currentRound];
    },
    // who is the next player to start a new question => lowest score first
    nextPlayerToStartAQuestion(state, getters) {
      return _minBy(getters.playersWithoutFirstTurn, 'score');
    },
    nextPlayerByOrder(state, getters) {
      if (getters.playersWithOpenTurns.length && getters.activePlayer) {
        const nextPlayer = getters.playersWithOpenTurns.find(player => {
          return player.id === getters.activePlayer.id + 1;
        });
        return nextPlayer || getters.playersWithOpenTurns[0];
      } else {
        return null;
      }
    },
    nextPlayerByLowestScore(state, getters) {
      return _minBy(getters.playersWithOpenTurns, 'score');
    },
    clockIsRunning(state) {
      return state.game.clockStarted !== false;
    },
    reviewModeEnabled(state) {
      return state.game.reviewMode;
    },
    inGalerij(state) {
      return state.game.currentRound === 3;
    },
    inFinale(state) {
      return state.game.currentRound === 5;
    },
    gameEnded(state) {
      return state.game.winnerId !== false;
    },
    allImgUrlsFromQuestions(state, getters) {
      return getters['openDeur/preloadImgUrls'].concat(
        getters['galerij/preloadImgUrls']
      );
    }
  }
});

export default store;
